import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import EmojiPicker from "emoji-picker-react";
import {
  Hash, Lock, Send, MessageSquare, Users, Plus, Search, Circle,
  Settings, Trash2, UserPlus, Loader2, X, ChevronDown,
  Paperclip, Smile, Image as ImageIcon, FileText, Download,
  Video, VideoOff, Mic, MicOff, PhoneOff, Phone, Monitor,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("token") || "";
}

async function apiFetch(path, opts = {}) {
  const sep = path.includes("?") ? "&" : "?";
  const url = `${API_URL}/api/chat${path}${sep}token=${getToken()}`;
  const res = await fetch(url, { headers: { "Content-Type": "application/json" }, ...opts });
  if (!res.ok) throw new Error((await res.json()).detail || "Request failed");
  return res.json();
}

async function uploadChatFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("token", getToken());
  const res = await fetch(`${API_URL}/api/chat/upload`, { method: "POST", body: formData });
  if (!res.ok) throw new Error("Upload failed");
  return res.json();
}

function timeFormat(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function Avatar({ name, role, online, size = "md" }) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const colors = {
    admin: "bg-red-100 text-red-700",
    dealer: "bg-teal/10 text-teal",
    staff: "bg-blue-100 text-blue-700",
    guest: "bg-orange-100 text-orange-600",
  };
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs";
  return (
    <div className="relative flex-shrink-0">
      <div className={`${sz} rounded-full flex items-center justify-center font-semibold ${colors[role] || "bg-slate-100 text-slate-600"}`}>
        {initials}
      </div>
      {online !== undefined && (
        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-400" : "bg-slate-300"}`} />
      )}
    </div>
  );
}

export default function ChatCenter({ isAdmin = false }) {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [dmThreads, setDmThreads] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeView, setActiveView] = useState(null); // {type: "channel"|"dm"|"guest", id, name}
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showNewDM, setShowNewDM] = useState(false);
  const [showChannelDialog, setShowChannelDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelForm, setChannelForm] = useState({ name: "", description: "", type: "public", members: [] });
  const [showMembers, setShowMembers] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);
  const [dmSearch, setDmSearch] = useState("");
  const [sideView, setSideView] = useState("channels"); // "channels" | "dms" | "support"
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showQuickAssist, setShowQuickAssist] = useState(false);
  const [guestSessions, setGuestSessions] = useState([]);
  // Video call state
  const [callState, setCallState] = useState(null); // null | {type:"incoming"|"outgoing"|"connected", userId, userName, threadId}
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Fetch channels & DMs
  const fetchData = useCallback(async () => {
    try {
      const [ch, dms, users] = await Promise.all([
        apiFetch("/channels"),
        apiFetch("/dm/threads"),
        apiFetch("/users"),
      ]);
      setChannels(ch);
      setDmThreads(dms);
      setAllUsers(users);
      if (isAdmin) {
        try {
          const sessions = await apiFetch("/guest/sessions");
          setGuestSessions(sessions);
        } catch {}
      }
    } catch {}
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // WebSocket connection
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const wsUrl = API_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/chat?token=${token}`);
    wsRef.current = ws;

    ws.onmessage = async (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "channel_message") {
        setMessages(prev => {
          if (prev.length > 0 && prev[0]?.channel_id === data.message.channel_id) {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          }
          return prev;
        });
        setChannels(prev => prev.map(ch =>
          ch.id === data.message.channel_id && data.message.sender_id !== user?.id
            ? { ...ch, unread_count: (ch.unread_count || 0) + 1 }
            : ch
        ));
        setTimeout(scrollToBottom, 100);
      } else if (data.type === "dm_message") {
        setMessages(prev => {
          if (prev.length > 0 && prev[0]?.dm_thread_id === data.message.dm_thread_id) {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          }
          return prev;
        });
        setDmThreads(prev => prev.map(t =>
          t.id === data.thread_id
            ? { ...t, last_message: data.message.content.slice(0, 100), last_message_at: data.message.created_at, unread_count: data.message.sender_id !== user?.id ? (t.unread_count || 0) + 1 : t.unread_count }
            : t
        ));
        setTimeout(scrollToBottom, 100);
      } else if (data.type === "typing") {
        setTypingUsers(prev => {
          const existing = prev.filter(t => t.user_id !== data.user_id);
          return [...existing, { ...data, ts: Date.now() }];
        });
      } else if (data.type === "user_online" || data.type === "user_offline") {
        setAllUsers(prev => prev.map(u =>
          u.id === data.user_id ? { ...u, online: data.type === "user_online" } : u
        ));
      } else if (data.type === "call_request") {
        setCallState({ type: "incoming", userId: data.from_user_id, userName: data.from_user_name, threadId: data.thread_id });
      } else if (data.type === "call_offer") {
        // Received offer, set remote description and create answer
        const pc = peerConnectionRef.current;
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          wsRef.current?.send(JSON.stringify({ type: "call_answer", target_user_id: data.from_user_id, answer: pc.localDescription }));
        }
      } else if (data.type === "call_answer") {
        const pc = peerConnectionRef.current;
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
      } else if (data.type === "ice_candidate") {
        const pc = peerConnectionRef.current;
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      } else if (data.type === "call_end" || data.type === "call_reject") {
        peerConnectionRef.current?.close();
        peerConnectionRef.current = null;
        localStream?.getTracks().forEach(t => t.stop());
        setLocalStream(null);
        setRemoteStream(null);
        setCallState(null);
        if (data.type === "call_reject") toast.info("Call declined");
      } else if (data.type === "guest_message") {
        // A guest sent a new message - update guest sessions
        setGuestSessions(prev => {
          const exists = prev.find(s => s.id === data.session_id);
          if (exists) {
            return prev.map(s => s.id === data.session_id
              ? { ...s, last_message: data.message.content?.slice(0, 100), last_message_at: data.message.created_at, unread_count: (s.unread_count || 0) + 1 }
              : s
            ).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
          }
          return [{ id: data.session_id, guest_name: data.guest_name, status: "active", last_message: data.message.content?.slice(0, 100), last_message_at: data.message.created_at, unread_count: 1, created_at: data.message.created_at }, ...prev];
        });
        // If viewing this guest session, add message
        setMessages(prev => {
          if (prev.length > 0 && prev[0]?.session_id === data.session_id) {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          }
          return prev;
        });
        setTimeout(scrollToBottom, 100);
      } else if (data.type === "guest_admin_reply") {
        // Another admin replied to a guest session
        setMessages(prev => {
          if (prev.length > 0 && prev[0]?.session_id === data.session_id) {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          }
          return prev;
        });
        setTimeout(scrollToBottom, 100);
      }
    };

    ws.onclose = () => {
      // Reconnect after 3 seconds
      setTimeout(() => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
      }, 3000);
    };

    // Keep alive ping every 25 seconds
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 25000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [user, scrollToBottom]);

  // Clean up old typing indicators
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers(prev => prev.filter(t => Date.now() - t.ts < 3000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load messages when active view changes
  useEffect(() => {
    if (!activeView) return;
    const loadMessages = async () => {
      try {
        let msgs;
        if (activeView.type === "channel") {
          msgs = await apiFetch(`/channels/${activeView.id}/messages`);
          setChannels(prev => prev.map(ch => ch.id === activeView.id ? { ...ch, unread_count: 0 } : ch));
        } else if (activeView.type === "guest") {
          msgs = await apiFetch(`/guest/sessions/${activeView.id}/messages`);
          setGuestSessions(prev => prev.map(s => s.id === activeView.id ? { ...s, unread_count: 0 } : s));
        } else {
          msgs = await apiFetch(`/dm/threads/${activeView.id}/messages`);
          setDmThreads(prev => prev.map(t => t.id === activeView.id ? { ...t, unread_count: 0 } : t));
        }
        setMessages(msgs);
        setTimeout(scrollToBottom, 100);
      } catch {}
    };
    loadMessages();
  }, [activeView, scrollToBottom]);

  const sendMessage = async () => {
    if (!input.trim() || !activeView) return;
    setSending(true);
    try {
      const content = input.trim();
      setInput("");
      let result;
      if (activeView.type === "channel") {
        result = await apiFetch(`/channels/${activeView.id}/messages`, {
          method: "POST", body: JSON.stringify({ content }),
        });
      } else if (activeView.type === "guest") {
        result = await apiFetch(`/guest/sessions/${activeView.id}/reply`, {
          method: "POST", body: JSON.stringify({ content }),
        });
      } else {
        result = await apiFetch(`/dm/threads/${activeView.id}/messages`, {
          method: "POST", body: JSON.stringify({ content }),
        });
      }
      if (result?.id) {
        setMessages(prev => prev.some(m => m.id === result.id) ? prev : [...prev, result]);
        setTimeout(scrollToBottom, 50);
      }
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  const handleTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    const body = {};
    if (activeView?.type === "channel") body.channel_id = activeView.id;
    else if (activeView?.type === "dm") body.dm_thread_id = activeView.id;
    apiFetch("/typing", { method: "POST", body: JSON.stringify(body) }).catch(() => {});
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeView) return;
    e.target.value = "";
    setUploading(true);
    try {
      const fileData = await uploadChatFile(file);
      const body = { content: "", file_url: fileData.file_url, file_name: fileData.file_name, file_type: fileData.file_type };
      let result;
      if (activeView.type === "channel") {
        result = await apiFetch(`/channels/${activeView.id}/messages`, { method: "POST", body: JSON.stringify(body) });
      } else {
        result = await apiFetch(`/dm/threads/${activeView.id}/messages`, { method: "POST", body: JSON.stringify(body) });
      }
      if (result?.id) {
        setMessages(prev => prev.some(m => m.id === result.id) ? prev : [...prev, result]);
        setTimeout(scrollToBottom, 50);
      }
    } catch { toast.error("File upload failed"); }
    setUploading(false);
  };

  const onEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    setShowEmoji(false);
  };

  // ====== WebRTC Video Calling ======
  const ICE_SERVERS = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] };

  const startCall = async (targetUserId, targetUserName, threadId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      setCallState({ type: "outgoing", userId: targetUserId, userName: targetUserName, threadId });
      // Send call request via WebSocket
      wsRef.current?.send(JSON.stringify({ type: "call_request", target_user_id: targetUserId, thread_id: threadId }));
    } catch (err) {
      toast.error("Could not access camera/microphone");
    }
  };

  const acceptCall = async () => {
    if (!callState || callState.type !== "incoming") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      pc.ontrack = (e) => { setRemoteStream(e.streams[0]); };
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          wsRef.current?.send(JSON.stringify({ type: "ice_candidate", target_user_id: callState.userId, candidate: e.candidate }));
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: "call_offer", target_user_id: callState.userId, offer: pc.localDescription }));
      setCallState(prev => ({ ...prev, type: "connected" }));
    } catch { toast.error("Could not access camera/microphone"); endCall(); }
  };

  const endCall = () => {
    if (callState?.userId) {
      wsRef.current?.send(JSON.stringify({ type: "call_end", target_user_id: callState.userId }));
    }
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;
    localStream?.getTracks().forEach(t => t.stop());
    setLocalStream(null);
    setRemoteStream(null);
    setCallState(null);
    setVideoEnabled(true);
    setAudioEnabled(true);
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setVideoEnabled(prev => !prev);
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setAudioEnabled(prev => !prev);
    }
  };

  // Attach streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream;
  }, [localStream]);
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) remoteVideoRef.current.srcObject = remoteStream;
  }, [remoteStream]);

  const startDM = async (otherUser) => {
    try {
      const thread = await apiFetch("/dm/threads", {
        method: "POST", body: JSON.stringify({ user_id: otherUser.id }),
      });
      setShowNewDM(false);
      setDmSearch("");
      await fetchData();
      setActiveView({ type: "dm", id: thread.id, name: `${otherUser.first_name} ${otherUser.last_name}` });
      setSideView("dms");
    } catch (e) { toast.error(e.message); }
  };

  const saveChannel = async () => {
    try {
      if (editingChannel) {
        await apiFetch(`/channels/${editingChannel.id}`, {
          method: "PUT", body: JSON.stringify(channelForm),
        });
        toast.success("Channel updated");
      } else {
        await apiFetch("/channels", {
          method: "POST", body: JSON.stringify(channelForm),
        });
        toast.success("Channel created");
      }
      setShowChannelDialog(false);
      setEditingChannel(null);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const deleteChannel = async (channelId) => {
    if (!window.confirm("Delete this channel and all its messages?")) return;
    try {
      await apiFetch(`/channels/${channelId}`, { method: "DELETE" });
      toast.success("Channel deleted");
      if (activeView?.id === channelId) setActiveView(null);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const openEditChannel = (ch) => {
    setEditingChannel(ch);
    setChannelForm({ name: ch.name, description: ch.description, type: ch.type, members: ch.members || [] });
    setShowChannelDialog(true);
  };

  const activeTyping = typingUsers.filter(t => {
    if (activeView?.type === "channel") return t.channel_id === activeView.id;
    if (activeView?.type === "dm") return t.dm_thread_id === activeView.id;
    return false;
  });

  const totalUnread = channels.reduce((sum, ch) => sum + (ch.unread_count || 0), 0) +
    dmThreads.reduce((sum, t) => sum + (t.unread_count || 0), 0) +
    guestSessions.reduce((sum, s) => sum + (s.unread_count || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-[70vh]">
      <Loader2 className="w-8 h-8 animate-spin text-teal" />
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="chat-center">
      {/* Sidebar */}
      <div className="w-64 border-r border-slate-200 flex flex-col bg-slate-50 flex-shrink-0">
        {/* Tab switcher */}
        <div className="flex border-b border-slate-200">
          <button onClick={() => setSideView("channels")} className={`flex-1 py-2.5 text-xs font-semibold text-center transition-colors ${sideView === "channels" ? "text-teal border-b-2 border-teal bg-white" : "text-slate-500 hover:text-slate-700"}`} data-testid="chat-channels-tab">
            <Hash className="w-3.5 h-3.5 inline mr-1" />Channels
            {channels.reduce((s, c) => s + (c.unread_count || 0), 0) > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{channels.reduce((s, c) => s + (c.unread_count || 0), 0)}</span>
            )}
          </button>
          <button onClick={() => setSideView("dms")} className={`flex-1 py-2.5 text-xs font-semibold text-center transition-colors ${sideView === "dms" ? "text-teal border-b-2 border-teal bg-white" : "text-slate-500 hover:text-slate-700"}`} data-testid="chat-dms-tab">
            <MessageSquare className="w-3.5 h-3.5 inline mr-1" />DMs
            {dmThreads.reduce((s, t) => s + (t.unread_count || 0), 0) > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{dmThreads.reduce((s, t) => s + (t.unread_count || 0), 0)}</span>
            )}
          </button>
          {isAdmin && (
            <button onClick={() => { setSideView("support"); fetchData(); }} className={`flex-1 py-2.5 text-xs font-semibold text-center transition-colors ${sideView === "support" ? "text-teal border-b-2 border-teal bg-white" : "text-slate-500 hover:text-slate-700"}`} data-testid="chat-support-tab">
              <Circle className="w-3.5 h-3.5 inline mr-1" />Support
              {guestSessions.reduce((s, g) => s + (g.unread_count || 0), 0) > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{guestSessions.reduce((s, g) => s + (g.unread_count || 0), 0)}</span>
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {sideView === "channels" ? (
            <div className="p-2 space-y-0.5">
              {isAdmin && (
                <button onClick={() => { setEditingChannel(null); setChannelForm({ name: "", description: "", type: "public", members: [] }); setShowChannelDialog(true); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-teal hover:bg-teal/5 rounded-md transition-colors" data-testid="create-channel-btn">
                  <Plus className="w-3.5 h-3.5" /> New Channel
                </button>
              )}
              {channels.map(ch => (
                <button key={ch.id} onClick={() => setActiveView({ type: "channel", id: ch.id, name: ch.name, channelType: ch.type })}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors group ${activeView?.id === ch.id ? "bg-teal/10 text-teal" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`channel-${ch.id}`}>
                  {ch.type === "private" ? <Lock className="w-3.5 h-3.5 flex-shrink-0" /> : <Hash className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span className="text-sm font-medium truncate flex-1">{ch.name}</span>
                  {ch.unread_count > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{ch.unread_count}</span>}
                  {isAdmin && (
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button onClick={(e) => { e.stopPropagation(); openEditChannel(ch); }} className="p-0.5 hover:bg-slate-200 rounded"><Settings className="w-3 h-3" /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteChannel(ch.id); }} className="p-0.5 hover:bg-red-100 rounded text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : sideView === "support" ? (
            <div className="p-2 space-y-0.5">
              {guestSessions.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No support chats yet</p>
              ) : (
                guestSessions.map(s => (
                  <button key={s.id} onClick={() => setActiveView({ type: "guest", id: s.id, name: s.guest_name || "Visitor" })}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors ${activeView?.id === s.id ? "bg-teal/10" : "hover:bg-slate-100"}`}
                    data-testid={`guest-session-${s.id}`}>
                    <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {(s.guest_name || "V").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${activeView?.id === s.id ? "text-teal" : "text-slate-700"}`}>{s.guest_name || "Visitor"}</p>
                      <p className="text-xs text-slate-400 truncate">{s.last_message || "New session"}</p>
                    </div>
                    {s.unread_count > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{s.unread_count}</span>}
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              <button onClick={() => setShowNewDM(true)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-teal hover:bg-teal/5 rounded-md transition-colors" data-testid="new-dm-btn">
                <Plus className="w-3.5 h-3.5" /> New Message
              </button>
              {dmThreads.map(t => (
                <button key={t.id} onClick={() => setActiveView({ type: "dm", id: t.id, name: `${t.other_user?.first_name || ""} ${t.other_user?.last_name || ""}` })}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors ${activeView?.id === t.id ? "bg-teal/10" : "hover:bg-slate-100"}`}
                  data-testid={`dm-${t.id}`}>
                  <Avatar name={`${t.other_user?.first_name} ${t.other_user?.last_name}`} role={t.other_user?.role} online={allUsers.find(u => u.id === t.other_user?.id)?.online} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${activeView?.id === t.id ? "text-teal" : "text-slate-700"}`}>{t.other_user?.first_name} {t.other_user?.last_name}</p>
                    <p className="text-xs text-slate-400 truncate">{t.last_message || "No messages yet"}</p>
                  </div>
                  {t.unread_count > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{t.unread_count}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeView ? (
          <>
            {/* Chat header */}
            <div className="h-14 border-b border-slate-200 px-4 flex items-center justify-between flex-shrink-0 bg-white">
              <div className="flex items-center gap-2">
                {activeView.type === "channel" ? (
                  activeView.channelType === "private" ? <Lock className="w-4 h-4 text-amber-500" /> : <Hash className="w-4 h-4 text-teal" />
                ) : activeView.type === "guest" ? (
                  <Circle className="w-4 h-4 text-orange-500" />
                ) : (
                  <MessageSquare className="w-4 h-4 text-teal" />
                )}
                <h3 className="font-semibold text-navy text-sm">{activeView.name}</h3>
                {activeView.type === "channel" && activeView.channelType === "private" && (
                  <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full">Private</span>
                )}
                {activeView.type === "guest" && (
                  <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full">Live Support</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {activeView.type === "dm" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => {
                      const thread = dmThreads.find(t => t.id === activeView.id);
                      if (thread?.other_user) startCall(thread.other_user.id, `${thread.other_user.first_name} ${thread.other_user.last_name}`, thread.id);
                    }} data-testid="video-call-btn" title="Video Call">
                      <Video className="w-4 h-4 text-teal" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowQuickAssist(true)} data-testid="screen-share-btn" title="Screen Share Guide">
                      <Monitor className="w-4 h-4 text-slate-500" />
                    </Button>
                  </>
                )}
                {activeView.type === "channel" && isAdmin && activeView.channelType === "private" && (
                <Button variant="ghost" size="sm" onClick={async () => {
                  try {
                    const data = await apiFetch(`/channels/${activeView.id}/members`);
                    setChannelMembers(data.members || []);
                    setShowMembers(true);
                  } catch {}
                }} data-testid="view-members-btn">
                  <Users className="w-4 h-4 mr-1" /> Members
                </Button>
              )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="messages-area">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <MessageSquare className="w-10 h-10 mb-2" />
                  <p className="text-sm">No messages yet. Start the conversation!</p>
                </div>
              )}
              {messages.map((msg, i) => {
                const isMe = activeView.type === "guest"
                  ? msg.sender_type === "admin"
                  : msg.sender_id === user?.id;
                const showAvatar = i === 0 || messages[i - 1]?.sender_id !== msg.sender_id || messages[i - 1]?.sender_type !== msg.sender_type;
                const avatarRole = activeView.type === "guest" ? (msg.sender_type === "admin" ? "admin" : "guest") : msg.sender_role;
                return (
                  <div key={msg.id} className={`flex gap-2.5 ${isMe ? "flex-row-reverse" : ""}`} data-testid={`msg-${msg.id}`}>
                    {showAvatar ? (
                      <Avatar name={msg.sender_name} role={avatarRole} size="sm" />
                    ) : (
                      <div className="w-7 flex-shrink-0" />
                    )}
                    <div className={`max-w-[70%] ${isMe ? "text-right" : ""}`}>
                      {showAvatar && (
                        <div className={`flex items-center gap-2 mb-0.5 ${isMe ? "justify-end" : ""}`}>
                          <span className="text-xs font-semibold text-navy">{msg.sender_name}</span>
                          <span className="text-[10px] text-slate-400">{timeFormat(msg.created_at)}</span>
                        </div>
                      )}
                      <div className={`inline-block px-3 py-2 rounded-xl text-sm ${isMe ? "bg-teal text-white rounded-tr-sm" : "bg-slate-100 text-slate-700 rounded-tl-sm"}`}>
                        {msg.content && <p>{msg.content}</p>}
                        {msg.file_url && msg.file_type?.startsWith("image/") && (
                          <a href={`${API_URL}${msg.file_url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${API_URL}${msg.file_url}`} alt={msg.file_name} className="max-w-[240px] max-h-[180px] rounded-lg mt-1 object-cover" />
                          </a>
                        )}
                        {msg.file_url && !msg.file_type?.startsWith("image/") && (
                          <a href={`${API_URL}${msg.file_url}`} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-2 mt-1 px-2 py-1.5 rounded-lg ${isMe ? "bg-white/20 hover:bg-white/30" : "bg-white hover:bg-slate-50"} transition-colors`}>
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs truncate max-w-[160px]">{msg.file_name}</span>
                            <Download className="w-3.5 h-3.5 flex-shrink-0 ml-auto" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing indicator */}
            {activeTyping.length > 0 && (
              <div className="px-4 py-1 text-xs text-slate-400 italic">
                {activeTyping.map(t => t.user_name).join(", ")} {activeTyping.length === 1 ? "is" : "are"} typing...
              </div>
            )}

            {/* Input */}
            <div className="border-t border-slate-200 p-3 bg-white" data-testid="message-input-area">
              {showEmoji && activeView.type !== "guest" && (
                <div className="absolute bottom-16 left-72 z-50">
                  <EmojiPicker onEmojiClick={onEmojiClick} width={320} height={350} skinTonesDisabled searchDisabled previewConfig={{ showPreview: false }} />
                </div>
              )}
              <div className="flex items-center gap-2">
                {activeView.type !== "guest" && (
                  <>
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" data-testid="chat-file-input" />
                    <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Attach file" data-testid="attach-file-btn">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowEmoji(prev => !prev)} title="Emoji" data-testid="emoji-btn">
                      <Smile className="w-4 h-4 text-slate-400" />
                    </Button>
                  </>
                )}
                <Input
                  value={input}
                  onChange={(e) => { setInput(e.target.value); if (activeView.type !== "guest") handleTyping(); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={activeView.type === "guest" ? `Reply to ${activeView.name}...` : `Message ${activeView.name}...`}
                  className="flex-1"
                  data-testid="message-input"
                  onClick={() => setShowEmoji(false)}
                />
                <Button onClick={sendMessage} disabled={!input.trim() || sending} className="bg-teal hover:bg-teal/90 text-white" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p className="font-medium text-slate-500">Select a channel or conversation</p>
              <p className="text-sm mt-1">Choose from the sidebar to start chatting</p>
            </div>
          </div>
        )}
      </div>

      {/* New DM Dialog */}
      <Dialog open={showNewDM} onOpenChange={setShowNewDM}>
        <DialogContent className="sm:max-w-md" data-testid="new-dm-dialog">
          <DialogHeader><DialogTitle className="text-navy">New Message</DialogTitle></DialogHeader>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input value={dmSearch} onChange={(e) => setDmSearch(e.target.value)} placeholder="Search users..." className="pl-9" data-testid="dm-search-input" />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {allUsers.filter(u => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(dmSearch.toLowerCase())).map(u => (
              <button key={u.id} onClick={() => startDM(u)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left" data-testid={`dm-user-${u.id}`}>
                <Avatar name={`${u.first_name} ${u.last_name}`} role={u.role} online={u.online} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy">{u.first_name} {u.last_name}</p>
                  <p className="text-xs text-slate-400">{u.role} - {u.email}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Channel Create/Edit Dialog (Admin) */}
      {isAdmin && (
        <Dialog open={showChannelDialog} onOpenChange={setShowChannelDialog}>
          <DialogContent className="sm:max-w-md" data-testid="channel-dialog">
            <DialogHeader><DialogTitle className="text-navy">{editingChannel ? "Edit Channel" : "Create Channel"}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Channel Name *</Label>
                <Input value={channelForm.name} onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })} placeholder="e.g. general" data-testid="channel-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={channelForm.description} onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })} placeholder="What's this channel about?" data-testid="channel-desc-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={channelForm.type} onValueChange={(v) => setChannelForm({ ...channelForm, type: v })}>
                  <SelectTrigger data-testid="channel-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public - Everyone can see</SelectItem>
                    <SelectItem value="private">Private - Invite only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {channelForm.type === "private" && (
                <div>
                  <Label>Members</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1 mt-1">
                    {allUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={channelForm.members.includes(u.id)}
                          onChange={(e) => {
                            const newMembers = e.target.checked
                              ? [...channelForm.members, u.id]
                              : channelForm.members.filter(id => id !== u.id);
                            setChannelForm({ ...channelForm, members: newMembers });
                          }}
                          className="rounded border-slate-300" />
                        <span>{u.first_name} {u.last_name}</span>
                        <span className="text-xs text-slate-400">({u.role})</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{channelForm.members.length} member(s) selected</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChannelDialog(false)}>Cancel</Button>
              <Button onClick={saveChannel} disabled={!channelForm.name.trim()} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-channel-btn">
                {editingChannel ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Members Dialog */}
      <Dialog open={showMembers} onOpenChange={setShowMembers}>
        <DialogContent className="sm:max-w-sm" data-testid="members-dialog">
          <DialogHeader><DialogTitle className="text-navy">Channel Members</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {channelMembers.map(m => (
              <div key={m.id} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg">
                <Avatar name={`${m.first_name} ${m.last_name}`} role={m.role} size="sm" />
                <div>
                  <p className="text-sm font-medium text-navy">{m.first_name} {m.last_name}</p>
                  <p className="text-xs text-slate-400">{m.role}</p>
                </div>
              </div>
            ))}
            {channelMembers.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No members</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Call Overlay */}
      {callState && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" data-testid="video-call-overlay">
          <div className="bg-slate-900 rounded-2xl p-6 max-w-2xl w-full mx-4">
            {callState.type === "incoming" ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-teal/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Phone className="w-8 h-8 text-teal" />
                </div>
                <p className="text-white text-lg font-semibold mb-1">{callState.userName}</p>
                <p className="text-slate-400 text-sm mb-6">Incoming video call...</p>
                <div className="flex justify-center gap-4">
                  <Button onClick={acceptCall} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full w-14 h-14" data-testid="accept-call-btn">
                    <Phone className="w-6 h-6" />
                  </Button>
                  <Button onClick={() => {
                    wsRef.current?.send(JSON.stringify({ type: "call_reject", target_user_id: callState.userId }));
                    setCallState(null);
                  }} className="bg-red-500 hover:bg-red-600 text-white rounded-full w-14 h-14" data-testid="reject-call-btn">
                    <PhoneOff className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative bg-slate-800 rounded-xl overflow-hidden aspect-video">
                    <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    {!remoteStream && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Users className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-400 text-sm">{callState.type === "outgoing" ? "Calling..." : "Connecting..."}</p>
                        </div>
                      </div>
                    )}
                    <p className="absolute bottom-2 left-2 text-white text-xs bg-black/50 px-2 py-1 rounded">{callState.userName}</p>
                  </div>
                  <div className="w-48 relative bg-slate-800 rounded-xl overflow-hidden">
                    <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                    {!videoEnabled && (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-700">
                        <VideoOff className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                    <p className="absolute bottom-2 left-2 text-white text-xs bg-black/50 px-2 py-1 rounded">You</p>
                  </div>
                </div>
                <div className="flex justify-center gap-3">
                  <Button onClick={toggleVideo} variant="ghost" className={`rounded-full w-12 h-12 ${videoEnabled ? "bg-slate-700 hover:bg-slate-600" : "bg-red-500/80 hover:bg-red-500"}`} data-testid="toggle-video-btn">
                    {videoEnabled ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                  </Button>
                  <Button onClick={toggleAudio} variant="ghost" className={`rounded-full w-12 h-12 ${audioEnabled ? "bg-slate-700 hover:bg-slate-600" : "bg-red-500/80 hover:bg-red-500"}`} data-testid="toggle-audio-btn">
                    {audioEnabled ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
                  </Button>
                  <Button onClick={endCall} className="bg-red-500 hover:bg-red-600 text-white rounded-full w-12 h-12" data-testid="end-call-btn">
                    <PhoneOff className="w-5 h-5" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Quick Assist Screen Sharing Guide */}
      <Dialog open={showQuickAssist} onOpenChange={setShowQuickAssist}>
        <DialogContent className="sm:max-w-lg" data-testid="quick-assist-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy flex items-center gap-2">
              <Monitor className="w-5 h-5" /> Screen Sharing with Quick Assist
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Quick Assist is a free Windows tool that lets you view or control another person's computer remotely. It's built into Windows 10 and 11.
            </p>
            <div className="space-y-3">
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 text-teal font-bold text-sm">1</div>
                <div>
                  <p className="text-sm font-medium text-navy">Open Quick Assist</p>
                  <p className="text-xs text-slate-500">Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-navy font-mono text-[11px]">Ctrl + Win + Q</kbd> or search "Quick Assist" in the Start menu</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 text-teal font-bold text-sm">2</div>
                <div>
                  <p className="text-sm font-medium text-navy">Helper (person viewing)</p>
                  <p className="text-xs text-slate-500">Click "Help someone" and sign in with your Microsoft account. You'll get a 6-digit security code.</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 text-teal font-bold text-sm">3</div>
                <div>
                  <p className="text-sm font-medium text-navy">Sharer (person sharing screen)</p>
                  <p className="text-xs text-slate-500">Open Quick Assist, enter the 6-digit code from the helper, and click "Share screen".</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 text-teal font-bold text-sm">4</div>
                <div>
                  <p className="text-sm font-medium text-navy">Choose access level</p>
                  <p className="text-xs text-slate-500">Select "Full control" or "View screen" and click "Continue". The helper can now see the screen.</p>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700"><strong>Note:</strong> Quick Assist requires both users to be on Windows 10 or 11. For Mac users, consider using the built-in "Screen Sharing" app or a tool like AnyDesk.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowQuickAssist(false)} className="bg-teal hover:bg-teal/90 text-white">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
